import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useIntercom } from "react-use-intercom";
import NavigationComponent from "go-app/components/Navigation";
import { HelpItem } from "go-app/components/Navigation/NavigationHelpComponent";
import { MenuAppConfig, WebApp } from "go-app/services/types";
import { ReactComponent as ChatSVG } from "go-core/images/svg/navigation/helpNavigation/chat.svg";
import { isWebView } from "go-core/utils/utils";
import { ConfigContext } from "../../services/Config/context";
import { IntercomApp } from "../../services/Intercom/intercomApp";
import { hasPermission, selectOrganization } from "../../services/organizations/selectors";
import { selectIsAdmin, selectUser } from "../../services/users/selectors";
import NavigationOrganizationMenu from "./components/NavigationOrganizationMenu";
import NavigationProfile from "./components/NavigationProfile/NavigationProfile";
import NavigationUserMenu from "./components/NavigationUserMenu";

const getShouldRenderUserNavigation = (pathname: string, organizationId?: number) => {
	const pathnameArray = pathname.split("/");
	const possibleOrganizationIdFromPathname = pathnameArray[1];

	return !organizationId && (isNaN(Number(possibleOrganizationIdFromPathname)) || pathname === "/");
};

const NavigationType = () => {
	const isAdmin = useSelector(selectIsAdmin);
	const user = useSelector(selectUser);
	const organization = useSelector(selectOrganization);
	const { pathname } = useLocation();
	const shouldRenderUserNavigation = getShouldRenderUserNavigation(pathname, organization.id);

	if (shouldRenderUserNavigation) {
		return <NavigationUserMenu user={user} isAdmin={isAdmin} />;
	} else if (organization.id) {
		return <NavigationOrganizationMenu organization={organization} />;
	}

	return null;
};

const Navigation = () => {
	const { show } = useIntercom();
	const { t } = useTranslation();
	const user = useSelector(selectUser);
	const organization = useSelector(selectOrganization);
	const isAdmin = useSelector(selectIsAdmin);
	const configService = useContext(ConfigContext);
	const applications = configService.applications();
	const settingsPermission = useSelector(hasPermission("SETTINGS"));
	const helpOptions: HelpItem[] = [];
	const menuAppConfig: MenuAppConfig = {
		webApp: WebApp.GOACCOUNTS,
		userEnteredLocation: organization.company_id !== "",
		oldVersionAppsTimestamp: configService.getAppsOldVersionTimestamps(),
	};
	const { pathname } = useLocation();
	const shouldNotDisplayOrganizationNavigationYet =
		!getShouldRenderUserNavigation(pathname, organization.id) && !organization.id;

	if (!user || isWebView || shouldNotDisplayOrganizationNavigationYet) {
		return <></>;
	}

	const renderProfile = () => {
		return <NavigationProfile user={user} organization={organization} isAdmin={isAdmin} />;
	};

	if (IntercomApp.isInitialized()) {
		helpOptions.push({
			name: t("lib:go_component.navigation.open_chat"),
			onClick: () => show(),
			icon: <ChatSVG />,
			isOnTop: true,
		});
	}

	return (
		<NavigationComponent
			apps_link_show_all={`/${organization.id}/apps/active`}
			apps_fetch_installed={async () => []}
			profile_name={user.name ? user.name : user.email}
			profile_avatar={user.avatar_link?.small ? user.avatar_link.small : undefined}
			organization_name={organization.name}
			company_id={organization.company_id}
			apps={applications}
			logo={"/graphics/goaccounts-logo.svg"}
			logo_small={"/graphics/goaccounts-sign-primary.svg"}
			isAdmin={isAdmin}
			accessServiceRouteLink={organization.id ? `/${organization.id}/help` : undefined}
			profile_content={renderProfile()}
			help_options={helpOptions}
			organization={organization}
			menuAppConfig={menuAppConfig}
			settingsPermission={settingsPermission}
		>
			<NavigationType />
		</NavigationComponent>
	);
};

export default Navigation;
